import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CircularProgress } from '@occc/ui-design-system';
import { useChainId } from '@occc-app/Chain/hooks';
import { CreateCampaignPage } from '@occc-app/routes/Campaign/CampaignCreatePage/CreateCampaignPage';
import { useCampaignCreateMutation } from '@occc-app/dataAccessLayer/campaign/mutations';
import { CampaignFormValues } from '@occc-app/routes/Campaign/types';
import { validateCampaign } from '@occc-app/routes/Campaign/validation';
import { CampaignCreateMutation } from '@occc/graphql';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import { getOfferComponentType } from '@occc-app/offerHelpers/utils';
import { createRouteCreator } from '@occc-app/routes/CampaignComponents/utils';
import { useCopyCampaignComponent } from '@occc-app/offerHelpers/hooks/useCopyCampaignComponent';
import { useGetAudienceById } from '@occc-app/dataAccessLayer/audience/useGetAudienceById';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { prepareOffer } from '@occc-app/routes/CampaignComponents/component-details';

import { routeCreator } from '../utils';

export const CreateCampaignPageContainer: React.FC = () => {
  const chainId = useChainId();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: { componentIdToCopy: string } | undefined | null = location.state;
  const [offerCopyInProgress, setOfferCopyInProgress] = useState<boolean>();
  const componentIdToCopy = locationState?.componentIdToCopy;
  const copyCampaignComponent = useCopyCampaignComponent();
  const { data: offer } = useGetOfferById(componentIdToCopy, chainId, {
    skip: !componentIdToCopy,
  });

  const inAudience = offer?.constraints?.filter(constraintHelper.isInAudience)?.[0];
  const { data: audienceFromConstraint } = useGetAudienceById(inAudience?.audienceId, chainId);

  const onCompleted = async (mutation: CampaignCreateMutation) => {
    const campaignUuid = mutation.campaignCreate.uuid;

    if (componentIdToCopy && offer) {
      setOfferCopyInProgress(true);

      const offerData = offer ? prepareOffer(offer, undefined, audienceFromConstraint) : offer;
      const offerId = await copyCampaignComponent({
        offer,
        campaignUuid,
        constraints: offerData.preparedConstraints,
      });

      const offerType = getOfferComponentType(offer);
      setOfferCopyInProgress(false);

      if (offerId && offerType) {
        const routeCreator = createRouteCreator(offerType);
        navigate(routeCreator.updateWithTab(campaignUuid, offerId));
      }
    } else {
      navigate(routeCreator.details(campaignUuid));
    }
  };

  const [campaignCreateMutation] = useCampaignCreateMutation({ onCompleted });

  const campaignCreate = (values: CampaignFormValues) => {
    return campaignCreateMutation({
      variables: {
        input: {
          chainId,
          ...values,
        },
      },
    });
  };

  if (componentIdToCopy && offerCopyInProgress) {
    return <CircularProgress />;
  }

  return <CreateCampaignPage campaignCreate={campaignCreate} validate={validateCampaign} />;
};
